import { createAsyncThunk } from '@reduxjs/toolkit';
import { 
    GetDashboardUserMenuPermissions, 
    GetAllDashboardMenuPermissions,
    GetDashboardUserModuleAssignments,
    AssignDashboardModulesToUser,
    RemoveDashboardModulesFromUser,
    CreateDashboardMenuPermission,
    UpdateDashboardMenuPermission,
    DeleteDashboardMenuPermission
} from 'helpers/api_method';
import { toast } from 'react-toastify';

// Get current user's dashboard menu permissions
export const fetchUserMenuPermissions = createAsyncThunk(
    'menuPermissions/fetchUserMenuPermissions',
    async (cacheBust: boolean = false, { rejectWithValue }) => {
        try {
            const response: any = await GetDashboardUserMenuPermissions(cacheBust);
            
            // The response is already transformed by axios interceptor to response.data
            if (response && (response.success || response.data)) {
                return response.data || response;
            } else {
                return rejectWithValue(response?.message || 'Failed to fetch dashboard menu permissions');
            }
        } catch (error: any) {
            console.error('Error fetching dashboard user menu permissions:', error);
            return rejectWithValue(error?.message || 'Failed to fetch dashboard menu permissions');
        }
    }
);

// Get all dashboard menu permissions (admin only)
export const fetchAllMenuPermissions = createAsyncThunk(
    'menuPermissions/fetchAllMenuPermissions',
    async (_, { rejectWithValue }) => {
        try {
            const response: any = await GetAllDashboardMenuPermissions();
            // The response is already transformed by axios interceptor to response.data
            if (response && (response.success || response.data)) {
                return response.data || response;
            } else {
                return rejectWithValue(response?.message || 'Failed to fetch all dashboard menu permissions');
            }
        } catch (error: any) {
            console.error('Error fetching all dashboard menu permissions:', error);
            return rejectWithValue(error?.message || 'Failed to fetch all dashboard menu permissions');
        }
    }
);

// Get dashboard user module assignments
export const fetchUserModuleAssignments = createAsyncThunk(
    'menuPermissions/fetchUserModuleAssignments',
    async (userId: string, { rejectWithValue }) => {
        try {
            const response: any = await GetDashboardUserModuleAssignments(userId);
            // The response is already transformed by axios interceptor to response.data
            if (response && (response.success || response.data)) {
                return response.data || response;
            } else {
                return rejectWithValue(response?.message || 'Failed to fetch dashboard user module assignments');
            }
        } catch (error: any) {
            console.error('Error fetching dashboard user module assignments:', error);
            return rejectWithValue(error?.message || 'Failed to fetch dashboard user module assignments');
        }
    }
);

// Assign dashboard modules to user
export const assignModulesToUser = createAsyncThunk(
    'menuPermissions/assignModulesToUser',
    async (data: { user_id: string; module_ids: string[]; notes?: string }, { rejectWithValue }) => {
        try {
            const response: any = await AssignDashboardModulesToUser(data);
            // The response is already transformed by axios interceptor to response.data
            if (response && (response.success || response.data)) {
                toast.success('Dashboard modules assigned successfully');
                return response.data || response;
            } else {
                const errorMessage = response?.message || 'Failed to assign dashboard modules';
                toast.error(errorMessage);
                return rejectWithValue(errorMessage);
            }
        } catch (error: any) {
            const errorMessage = error?.message || 'Failed to assign dashboard modules';
            console.error('Error assigning dashboard modules to user:', error);
            toast.error(errorMessage);
            return rejectWithValue(errorMessage);
        }
    }
);

// Remove dashboard modules from user
export const removeModulesFromUser = createAsyncThunk(
    'menuPermissions/removeModulesFromUser',
    async (data: { user_id: string; module_ids: string[] }, { rejectWithValue }) => {
        try {
            const response: any = await RemoveDashboardModulesFromUser(data);
            // The response is already transformed by axios interceptor to response.data
            if (response && (response.success || response.data)) {
                toast.success('Dashboard modules removed successfully');
                return response.data || response;
            } else {
                const errorMessage = response?.message || 'Failed to remove dashboard modules';
                toast.error(errorMessage);
                return rejectWithValue(errorMessage);
            }
        } catch (error: any) {
            const errorMessage = error?.message || 'Failed to remove dashboard modules';
            console.error('Error removing dashboard modules from user:', error);
            toast.error(errorMessage);
            return rejectWithValue(errorMessage);
        }
    }
);

// Create dashboard menu permission
export const createMenuPermission = createAsyncThunk(
    'menuPermissions/createMenuPermission',
    async (data: any, { rejectWithValue }) => {
        try {
            const response: any = await CreateDashboardMenuPermission(data);
            if (response && (response.success || response.data)) {
                toast.success('Menu permission created successfully');
                return response.data || response;
            } else {
                const errorMessage = response?.message || 'Failed to create menu permission';
                toast.error(errorMessage);
                return rejectWithValue(errorMessage);
            }
        } catch (error: any) {
            const errorMessage = error?.message || 'Failed to create menu permission';
            console.error('Error creating menu permission:', error);
            toast.error(errorMessage);
            return rejectWithValue(errorMessage);
        }
    }
);

// Update dashboard menu permission
export const updateMenuPermission = createAsyncThunk(
    'menuPermissions/updateMenuPermission',
    async (data: { id: string; [key: string]: any }, { rejectWithValue }) => {
        try {
            const { id, ...updateData } = data;
            const response: any = await UpdateDashboardMenuPermission(id, updateData);
            if (response && (response.success || response.data)) {
                toast.success('Menu permission updated successfully');
                return response.data || response;
            } else {
                const errorMessage = response?.message || 'Failed to update menu permission';
                toast.error(errorMessage);
                return rejectWithValue(errorMessage);
            }
        } catch (error: any) {
            const errorMessage = error?.message || 'Failed to update menu permission';
            console.error('Error updating menu permission:', error);
            toast.error(errorMessage);
            return rejectWithValue(errorMessage);
        }
    }
);

// Delete dashboard menu permission
export const deleteMenuPermission = createAsyncThunk(
    'menuPermissions/deleteMenuPermission',
    async (id: string, { rejectWithValue }) => {
        try {
            const response: any = await DeleteDashboardMenuPermission(id);
            if (response && (response.success || response.data)) {
                toast.success('Menu permission deleted successfully');
                return response.data || response;
            } else {
                const errorMessage = response?.message || 'Failed to delete menu permission';
                toast.error(errorMessage);
                return rejectWithValue(errorMessage);
            }
        } catch (error: any) {
            const errorMessage = error?.message || 'Failed to delete menu permission';
            console.error('Error deleting menu permission:', error);
            toast.error(errorMessage);
            return rejectWithValue(errorMessage);
        }
    }
);
